import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { collection, getDocs } from "firebase/firestore";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../firebase";
import { loadGoogleMaps } from "../mapLoader";
import {
  ArrowLeft,
  MapPin,
  Search,
  X,
  Navigation,
  ShieldCheck,
  Star,
  SlidersHorizontal,
} from "lucide-react";

/* ---------------- helpers ---------------- */

function makeAvatar(name) {
  const initials = (name || "D")
    .split(" ")
    .map((n) => n?.[0] || "")
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const colors = ["#3b82f6", "#6366f1", "#8b5cf6", "#10b981"];
  const bg = colors[(name || "").length % colors.length];

  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
      <rect width="200" height="200" fill="${bg}"/>
      <text x="50%" y="50%" text-anchor="middle" dy=".35em" fill="#fff" font-size="80" font-family="Arial" font-weight="bold">${initials}</text>
    </svg>`
  )}`;
}

function normalizeDoctor(raw) {
  if (!raw) return null;

  const safeName =
    typeof raw.name === "string" && raw.name.trim() ? raw.name : "Doctor";

  const safeSpecialty =
    typeof raw.specialty === "string" && raw.specialty.trim()
      ? raw.specialty
      : "General";

  const safeCity = typeof raw.city === "string" ? raw.city : "";

  const safePhoto =
    typeof raw.photoURL === "string" && raw.photoURL.trim()
      ? raw.photoURL
      : makeAvatar(safeName);

  const safeRating =
    Number.isFinite(Number(raw.rating)) ? Number(raw.rating) : 4.5;

  const safeExperience =
    raw.experience != null ? String(raw.experience) : "5+";

  const lat = Number(raw?.position?.lat);
  const lng = Number(raw?.position?.lng);

  return {
    id: raw.id,
    name: safeName,
    specialty: safeSpecialty,
    specialtyKey: raw.specialtyKey || safeSpecialty,
    city: safeCity,
    rating: parseFloat(safeRating.toFixed(1)),
    feeText: typeof raw.feeText === "string" ? raw.feeText : "",
    experience: safeExperience,
    verified: raw.verified === true,
    mode: typeof raw.mode === "string" ? raw.mode : "",
    position: {
      lat: Number.isFinite(lat) ? lat : 41.3275,
      lng: Number.isFinite(lng) ? lng : 19.8187,
    },
    photoURL: safePhoto,
    dist: Number.isFinite(Number(raw.dist)) ? Number(raw.dist) : null,
  };
}

function distKm(a, b) {
  if (!a || !b) return Infinity;

  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lng - a.lng);

  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) *
      Math.cos(toRad(b.lat)) *
      Math.sin(dLon / 2) ** 2;

  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

function toSafeNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function clampText(v, fallback = "") {
  return typeof v === "string" ? v : fallback;
}

function roundRadiusKm(km) {
  if (!Number.isFinite(km)) return 0;
  if (km < 1) return Math.round(km * 10) / 10;
  if (km < 10) return Math.round(km * 2) / 2;
  return Math.round(km);
}

/* Approximate visible map radius based on current viewport.
   Uses center -> top-right corner distance, then halves it a bit to feel natural. */
function getVisibleRadiusKm(map) {
  try {
    const bounds = map?.getBounds?.();
    const center = map?.getCenter?.();
    if (!bounds || !center) return null;

    const ne = bounds.getNorthEast();
    const centerPoint = { lat: center.lat(), lng: center.lng() };
    const edgePoint = { lat: ne.lat(), lng: ne.lng() };

    const diagonal = distKm(centerPoint, edgePoint);
    const visibleRadius = diagonal * 0.72;

    return roundRadiusKm(visibleRadius);
  } catch {
    return null;
  }
}

const MAP_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#f8f7ff" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#4338ca" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#e0e7ff" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#c7d2fe" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#a5b4fc" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#bfdbfe" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#ede9fe" }] },
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
];

function makeMarkerSvg(selected) {
  const bg = selected ? "#4338ca" : "#6366f1";
  const s = selected ? 44 : 36;

  return {
    url: `data:image/svg+xml,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s + 8}" viewBox="0 0 ${s} ${s + 8}">
        <circle cx="${s / 2}" cy="${s / 2}" r="${s / 2 - 2}" fill="${bg}" stroke="white" stroke-width="3"/>
        <polygon points="${s / 2 - 6},${s - 2} ${s / 2 + 6},${s - 2} ${s / 2},${s + 8}" fill="${bg}"/>
      </svg>`
    )}`,
    scaledSize: { width: s, height: s + 8 },
    anchor: { x: s / 2, y: s + 8 },
  };
}

/* ---------------- UI small components ---------------- */

function Chip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        fontSize: 10,
        fontWeight: 800,
        background: active ? "#6366f1" : "#f5f3ff",
        color: active ? "#fff" : "#4338ca",
        border: `1.5px solid ${active ? "#6366f1" : "#c7d2fe"}`,
        borderRadius: 20,
        padding: "4px 10px",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

function DoctorCardListItem({ doctor, selected, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: "12px 14px",
        borderBottom: "1px solid #f0f0f8",
        cursor: "pointer",
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        background: selected ? "#eef2ff" : "#fff",
        borderLeft: `3px solid ${selected ? "#6366f1" : "transparent"}`,
        transition: "background .15s",
      }}
    >
      <div style={{ position: "relative", flexShrink: 0 }}>
        <img
          src={doctor.photoURL}
          alt={doctor.name}
          onError={(e) => {
            e.currentTarget.src = makeAvatar(doctor.name);
          }}
          style={{
            width: 46,
            height: 46,
            borderRadius: 12,
            objectFit: "cover",
            border: "2px solid #e0e7ff",
            display: "block",
          }}
        />
        {doctor.verified && (
          <div
            style={{
              position: "absolute",
              bottom: -2,
              right: -2,
              background: "#6366f1",
              borderRadius: "50%",
              width: 14,
              height: 14,
              border: "2px solid #fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ShieldCheck size={7} color="#fff" />
          </div>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 900,
            color: "#1e1b4b",
            marginBottom: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {doctor.name}
        </div>

        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#6366f1",
            marginBottom: 5,
          }}
        >
          {doctor.specialty}
        </div>

        <div
          style={{
            display: "flex",
            gap: 4,
            flexWrap: "wrap",
            marginBottom: 4,
          }}
        >
          <span
            style={{
              fontSize: 9,
              fontWeight: 800,
              background: "#f5f3ff",
              color: "#4338ca",
              borderRadius: 5,
              padding: "2px 6px",
              display: "inline-flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Star size={8} color="#f59e0b" fill="#f59e0b" />
            {doctor.rating}
          </span>

          {doctor.feeText && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 900,
                color: "#059669",
                background: "#dcfce7",
                borderRadius: 5,
                padding: "2px 6px",
              }}
            >
              {doctor.feeText}
            </span>
          )}
        </div>

        <div
          style={{
            fontSize: 9,
            fontWeight: 800,
            color: "#64748b",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <div
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: "#10b981",
              flexShrink: 0,
            }}
          />
          {Number.isFinite(doctor.dist)
            ? `${doctor.dist.toFixed(1)} km larg`
            : "Distancë e panjohur"}
          {doctor.city ? ` · ${doctor.city}` : ""}
        </div>
      </div>
    </div>
  );
}

function DoctorPopup({ doctor, isMobile, navigate, onClose }) {
  if (!doctor || !doctor.id) return null;

  const safeName =
    typeof doctor.name === "string" && doctor.name.trim()
      ? doctor.name
      : "Doctor";

  const safeSpecialty =
    typeof doctor.specialty === "string" && doctor.specialty.trim()
      ? doctor.specialty
      : "General";

  const safeCity = typeof doctor.city === "string" ? doctor.city : "";

  const safePhoto =
    typeof doctor.photoURL === "string" && doctor.photoURL.trim()
      ? doctor.photoURL
      : makeAvatar(safeName);

  const safeRating =
    Number.isFinite(Number(doctor.rating)) ? Number(doctor.rating) : 4.5;

  const safeFee =
    typeof doctor.feeText === "string" ? doctor.feeText : "";

  const safeExperience =
    doctor.experience != null ? String(doctor.experience) : "5+";

  const safeDist =
    Number.isFinite(Number(doctor.dist)) ? Number(doctor.dist) : null;

  if (isMobile) {
    return (
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          background: "#fff",
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          borderTop: "1px solid #e0e7ff",
          boxShadow: "0 -8px 32px rgba(15,23,42,.18)",
          zIndex: 30,
          padding: "10px 12px calc(10px + env(safe-area-inset-bottom, 0px))",
          maxHeight: "38vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            width: 38,
            height: 4,
            borderRadius: 999,
            background: "#cbd5e1",
            margin: "0 auto 10px",
          }}
        />

        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 10,
            right: 12,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#94a3b8",
            padding: 0,
          }}
        >
          <X size={18} />
        </button>

        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <div style={{ position: "relative", flexShrink: 0 }}>
            <img
              src={safePhoto}
              alt={safeName}
              onError={(e) => {
                e.currentTarget.src = makeAvatar(safeName);
              }}
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                objectFit: "cover",
                border: "2px solid #e0e7ff",
                display: "block",
              }}
            />
            {doctor.verified && (
              <div
                style={{
                  position: "absolute",
                  bottom: -2,
                  right: -2,
                  background: "#6366f1",
                  borderRadius: "50%",
                  width: 16,
                  height: 16,
                  border: "2px solid #fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ShieldCheck size={8} color="#fff" />
              </div>
            )}
          </div>

          <div style={{ flex: 1, minWidth: 0, paddingRight: 24 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 900,
                color: "#1e1b4b",
                marginBottom: 3,
                lineHeight: 1.2,
              }}
            >
              {safeName}
            </div>

            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#6366f1",
                marginBottom: 8,
                lineHeight: 1.35,
              }}
            >
              {safeSpecialty}
              {safeCity ? ` · ${safeCity}` : ""}
              {safeDist != null ? ` · ${safeDist.toFixed(1)} km` : ""}
            </div>

            <div
              style={{
                display: "flex",
                gap: 6,
                marginBottom: 10,
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  background: "#f5f3ff",
                  color: "#4338ca",
                  borderRadius: 6,
                  padding: "3px 7px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <Star size={9} color="#f59e0b" fill="#f59e0b" />
                {safeRating.toFixed(1)}
              </span>

              {safeFee && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 900,
                    color: "#059669",
                    background: "#dcfce7",
                    borderRadius: 6,
                    padding: "3px 7px",
                  }}
                >
                  {safeFee}
                </span>
              )}

              {safeExperience && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    background: "#f5f3ff",
                    color: "#4338ca",
                    borderRadius: 6,
                    padding: "3px 7px",
                  }}
                >
                  {safeExperience} vjet
                </span>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <button
                onClick={() => navigate(`/doctor/${doctor.id}`)}
                style={{
                  background: "#6366f1",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "11px 10px",
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                Rezervo
              </button>

              <button
                onClick={() => navigate(`/doctor/${doctor.id}`)}
                style={{
                  background: "#f5f3ff",
                  color: "#4338ca",
                  border: "1.5px solid #c7d2fe",
                  borderRadius: 10,
                  padding: "11px 10px",
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Profili
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "absolute",
        left: "50%",
        bottom: 16,
        transform: "translateX(-50%)",
        background: "#fff",
        border: "1px solid #e0e7ff",
        borderRadius: 18,
        padding: 14,
        width: 320,
        maxWidth: "calc(100vw - 32px)",
        boxShadow: "0 8px 32px rgba(99,102,241,.15)",
        zIndex: 30,
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
      }}
    >
      <div style={{ position: "relative", flexShrink: 0 }}>
        <img
          src={safePhoto}
          alt={safeName}
          onError={(e) => {
            e.currentTarget.src = makeAvatar(safeName);
          }}
          style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            objectFit: "cover",
            border: "2px solid #e0e7ff",
            display: "block",
          }}
        />
        {doctor.verified && (
          <div
            style={{
              position: "absolute",
              bottom: -2,
              right: -2,
              background: "#6366f1",
              borderRadius: "50%",
              width: 16,
              height: 16,
              border: "2px solid #fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ShieldCheck size={8} color="#fff" />
          </div>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 900,
            color: "#1e1b4b",
            marginBottom: 2,
          }}
        >
          {safeName}
        </div>

        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#6366f1",
            marginBottom: 6,
          }}
        >
          {safeSpecialty}
          {safeCity ? ` · ${safeCity}` : ""}
          {safeDist != null ? ` · ${safeDist.toFixed(1)} km` : ""}
        </div>

        <div
          style={{
            display: "flex",
            gap: 5,
            marginBottom: 10,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              background: "#f5f3ff",
              color: "#4338ca",
              borderRadius: 6,
              padding: "2px 7px",
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Star size={9} color="#f59e0b" fill="#f59e0b" />
            {safeRating.toFixed(1)}
          </span>

          {safeFee && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 900,
                color: "#059669",
                background: "#dcfce7",
                borderRadius: 6,
                padding: "2px 7px",
              }}
            >
              {safeFee}
            </span>
          )}

          {safeExperience && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 800,
                background: "#f5f3ff",
                color: "#4338ca",
                borderRadius: 6,
                padding: "2px 7px",
              }}
            >
              {safeExperience} vjet
            </span>
          )}
        </div>

        <div style={{ display: "flex", gap: 7 }}>
          <button
            onClick={() => navigate(`/doctor/${doctor.id}`)}
            style={{
              flex: 1,
              background: "#6366f1",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              padding: "10px",
              fontSize: 12,
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            Rezervo →
          </button>

          <button
            onClick={() => navigate(`/doctor/${doctor.id}`)}
            style={{
              background: "#f5f3ff",
              color: "#4338ca",
              border: "1.5px solid #c7d2fe",
              borderRadius: 10,
              padding: "10px 12px",
              fontSize: 12,
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Profili
          </button>
        </div>
      </div>

      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#94a3b8",
          padding: 0,
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}

function MobileFiltersDrawer({
  open,
  onClose,
  search,
  setSearch,
  specialties,
  selectedSpec,
  setSelectedSpec,
  onClear,
}) {
  if (!open) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(15,23,42,.35)",
          zIndex: 40,
        }}
      />

      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          background: "#fff",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: "16px 16px calc(16px + env(safe-area-inset-bottom, 0px))",
          zIndex: 41,
          boxShadow: "0 -10px 40px rgba(15,23,42,.18)",
          maxHeight: "72vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            width: 42,
            height: 4,
            borderRadius: 999,
            background: "#cbd5e1",
            margin: "0 auto 14px",
          }}
        />

        <div
          style={{
            fontSize: 14,
            fontWeight: 900,
            color: "#1e1b4b",
            marginBottom: 12,
          }}
        >
          Filtra
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#f8f7ff",
            border: "1.5px solid #e0e7ff",
            borderRadius: 10,
            padding: "8px 12px",
            marginBottom: 12,
          }}
        >
          <Search size={13} color="#6366f1" style={{ flexShrink: 0 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Kërko emër, specialitet…"
            style={{
              border: "none",
              background: "transparent",
              outline: "none",
              fontSize: 13,
              fontWeight: 600,
              color: "#1e1b4b",
              width: "100%",
            }}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
                padding: 0,
              }}
            >
              <X size={13} color="#a5b4fc" />
            </button>
          )}
        </div>

        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              color: "#64748b",
              marginBottom: 8,
            }}
          >
            Specialiteti
          </div>

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Chip active={!selectedSpec} onClick={() => setSelectedSpec("")}>
              Të gjithë
            </Chip>

            {specialties.map((s) => (
              <Chip
                key={s.key}
                active={selectedSpec === s.key}
                onClick={() =>
                  setSelectedSpec(selectedSpec === s.key ? "" : s.key)
                }
              >
                {s.label}
              </Chip>
            ))}
          </div>
        </div>

        <button
          onClick={onClear}
          style={{
            width: "100%",
            background: "#fff",
            color: "#64748b",
            border: "1.5px solid #e2e8f0",
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 12,
            fontWeight: 800,
            cursor: "pointer",
          }}
        >
          Pastro filtrat
        </button>
      </div>
    </>
  );
}

/* ---------------- main component ---------------- */

export default function PatientMap() {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);

  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const DEFAULT_CENTER = useMemo(() => ({ lat: 41.3275, lng: 19.8187 }), []);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [isMapReady, setIsMapReady] = useState(false);

  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [userLocation, setUserLocation] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  const [search, setSearch] = useState("");
  const [selectedSpec, setSelectedSpec] = useState("");

  // real visible radius from map zoom/viewport
  const [visibleRadiusKm, setVisibleRadiusKm] = useState(25);

  const [locErr, setLocErr] = useState("");
  const [gettingLoc, setGettingLoc] = useState(false);

  const [showList, setShowList] = useState(() => window.innerWidth > 768);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const mapRef = useRef(null);
  const gmap = useRef(null);
  const markersRef = useRef([]);
  const circleRef = useRef(null);
  const userMarkerRef = useRef(null);
  const mapListenersRef = useRef([]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (isMobile) setShowList(false);
  }, [isMobile]);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      setError("Google Maps API key mungon.");
      return;
    }

    let alive = true;

    loadGoogleMaps(GOOGLE_MAPS_API_KEY).then((ok) => {
      if (!alive) return;
      if (!ok) setError("Nuk mund të ngarkohet Google Maps.");
      else setIsMapReady(true);
    });

    return () => {
      alive = false;
    };
  }, [GOOGLE_MAPS_API_KEY]);

  const updateVisibleRadiusFromMap = useCallback(() => {
    const map = gmap.current;
    if (!map) return;

    const next = getVisibleRadiusKm(map);
    if (next != null && next > 0) {
      setVisibleRadiusKm(next);

      const c = map.getCenter?.();
      if (c) {
        const nextCenter = { lat: c.lat(), lng: c.lng() };
        setCenter(nextCenter);
      }
    }
  }, []);

  const getUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocErr("Shfletuesi nuk mbështet gjeolokalizimin.");
      return;
    }

    setGettingLoc(true);
    setLocErr("");

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };

        setUserLocation(c);
        setCenter(c);

        if (gmap.current) {
          gmap.current.setCenter(c);
          gmap.current.setZoom(isMobile ? 13 : 12);
          setTimeout(updateVisibleRadiusFromMap, 250);
        }

        setGettingLoc(false);
      },
      (err) => {
        let msg = "Nuk mund të marrim vendndodhjen.";
        if (err?.code === 1) msg = "Lejo aksesin e vendndodhjes nga shfletuesi.";
        else if (err?.code === 2) msg = "Vendndodhja nuk është e disponueshme.";
        else if (err?.code === 3) msg = "Kërkesa për vendndodhje skadoi.";
        setLocErr(msg);
        setGettingLoc(false);
      },
      {
        timeout: 15000,
        enableHighAccuracy: true,
        maximumAge: 300000,
      }
    );
  }, [isMobile, updateVisibleRadiusFromMap]);

  useEffect(() => {
    getUserLocation();
  }, [getUserLocation]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const snap = await getDocs(collection(db, "doctors"));

        const rows = await Promise.all(
          snap.docs.map(async (docSnap) => {
            const data = docSnap.data();
            const coords = data?.location?.coordinates;

            const lat = toSafeNumber(coords?.lat);
            const lng = toSafeNumber(coords?.lng);

            if (lat == null || lng == null) return null;

            let photoURL =
              data.photoURL ||
              data.photoUrl ||
              data.profilePicture ||
              "";

            if (!photoURL) {
              try {
                const files = await listAll(ref(storage, `doctor_photos/${docSnap.id}`));
                if (files.items.length > 0) {
                  photoURL = await getDownloadURL(files.items[0]);
                }
              } catch {
                // ignore
              }
            }

            const specialtyLabel =
              clampText(data.specialty) ||
              clampText(data.specialtySq) ||
              clampText(data.category) ||
              "General";

            const specialtyKey =
              clampText(data.specialtyKey) ||
              clampText(data.specialty) ||
              clampText(data.specialtySq) ||
              clampText(data.category) ||
              "General";

            const ratingNum = toSafeNumber(data.rating);

            return {
              id: docSnap.id,
              name: clampText(data.name, "Doctor"),
              specialty: specialtyLabel,
              specialtyKey,
              city:
                clampText(data.city) ||
                clampText(data?.location?.address) ||
                "",
              rating: ratingNum != null ? parseFloat(ratingNum.toFixed(1)) : 4.5,
              feeText:
                clampText(data.feeText) ||
                clampText(data.priceText) ||
                "",
              experience: String(data.experienceYears ?? data.experience ?? "5+"),
              verified: data.isVerified === true || data.verified === true,
              mode: clampText(data.mode),
              position: { lat, lng },
              photoURL: photoURL || makeAvatar(data.name || "D"),
            };
          })
        );

        if (alive) setDoctors(rows.filter(Boolean));
      } catch (e) {
        console.error("Error loading doctors:", e);
        if (alive) setError("Nuk mund të ngarkojmë mjekët.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const specialties = useMemo(() => {
    const m = new Map();

    doctors.forEach((d) => {
      const key = d.specialtyKey || d.specialty || "";
      const label = d.specialty || d.specialtyKey || "";
      if (key && label) m.set(key, label);
    });

    return Array.from(m.entries()).map(([key, label]) => ({ key, label }));
  }, [doctors]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const refPoint = center || DEFAULT_CENTER;
    const effectiveRadius = visibleRadiusKm || 25;

    return doctors
      .map((d) => ({
        ...d,
        dist: distKm(refPoint, d.position),
      }))
      .filter((d) => {
        if (d.dist > effectiveRadius) return false;

        if (selectedSpec) {
          const doctorSpecKey = d.specialtyKey || d.specialty || "";
          if (doctorSpecKey !== selectedSpec) return false;
        }

        if (
          q &&
          !(
            (d.name || "").toLowerCase().includes(q) ||
            (d.specialty || "").toLowerCase().includes(q) ||
            (d.city || "").toLowerCase().includes(q)
          )
        ) {
          return false;
        }

        return true;
      })
      .sort((a, b) => a.dist - b.dist);
  }, [doctors, center, DEFAULT_CENTER, visibleRadiusKm, selectedSpec, search]);

  useEffect(() => {
    if (!selectedDoctor) return;
    const stillExists = filtered.some((d) => d.id === selectedDoctor.id);
    if (!stillExists) setSelectedDoctor(null);
  }, [filtered, selectedDoctor]);

  useEffect(() => {
    if (!isMapReady || !mapRef.current || gmap.current || !window.google?.maps) return;

    gmap.current = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: isMobile ? 13 : 12,
      styles: MAP_STYLES,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: false,
      clickableIcons: false,
      gestureHandling: "greedy",
    });

    circleRef.current = new window.google.maps.Circle({
      strokeColor: "#6366f1",
      strokeOpacity: 0.4,
      strokeWeight: 1.5,
      fillColor: "#6366f1",
      fillOpacity: 0.06,
      map: gmap.current,
      center,
      radius: (visibleRadiusKm || 25) * 1000,
    });

    const idleListener = gmap.current.addListener("idle", () => {
      updateVisibleRadiusFromMap();
    });

    const dragEndListener = gmap.current.addListener("dragend", () => {
      const c = gmap.current?.getCenter();
      if (!c) return;
      setCenter({ lat: c.lat(), lng: c.lng() });
    });

    const zoomChangedListener = gmap.current.addListener("zoom_changed", () => {
      const c = gmap.current?.getCenter();
      if (!c) return;
      setCenter({ lat: c.lat(), lng: c.lng() });
    });

    mapListenersRef.current = [idleListener, dragEndListener, zoomChangedListener];

    setTimeout(updateVisibleRadiusFromMap, 300);
  }, [isMapReady, center, visibleRadiusKm, isMobile, updateVisibleRadiusFromMap]);

  useEffect(() => {
    return () => {
      mapListenersRef.current.forEach((l) => l?.remove?.());
      mapListenersRef.current = [];
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
      circleRef.current?.setMap?.(null);
      userMarkerRef.current?.setMap?.(null);
    };
  }, []);

  useEffect(() => {
    if (!gmap.current || !circleRef.current) return;
    circleRef.current.setCenter(center);
    circleRef.current.setRadius((visibleRadiusKm || 25) * 1000);
  }, [center, visibleRadiusKm]);

  useEffect(() => {
    if (!gmap.current || !window.google?.maps) return;

    if (userMarkerRef.current) {
      userMarkerRef.current.setMap(null);
      userMarkerRef.current = null;
    }

    if (userLocation) {
      userMarkerRef.current = new window.google.maps.Marker({
        position: userLocation,
        map: gmap.current,
        title: "Vendndodhja juaj",
        zIndex: 100,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: "#2563eb",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 3,
        },
      });
    }
  }, [userLocation]);

  useEffect(() => {
    if (!gmap.current || !window.google?.maps) return;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    filtered.forEach((d) => {
      const isSel = selectedDoctor?.id === d.id;
      const icon = makeMarkerSvg(isSel);

      const marker = new window.google.maps.Marker({
        position: d.position,
        map: gmap.current,
        title: d.name,
        zIndex: isSel ? 50 : 10,
        optimized: true,
        icon: {
          url: icon.url,
          scaledSize: new window.google.maps.Size(
            icon.scaledSize.width,
            icon.scaledSize.height
          ),
          anchor: new window.google.maps.Point(icon.anchor.x, icon.anchor.y),
        },
      });

      marker.addListener("click", () => {
        try {
          const safeDoctor = normalizeDoctor(d);
          if (!safeDoctor) return;

          gmap.current?.panTo(safeDoctor.position);

          if (isMobile) {
            const currentZoom = gmap.current?.getZoom?.() || 12;
            gmap.current?.setZoom(Math.max(currentZoom, 14));
          }

          setSelectedDoctor(safeDoctor);
          setShowList(false);
        } catch (err) {
          console.error("Marker click failed:", err);
        }
      });

      markersRef.current.push(marker);
    });
  }, [filtered, selectedDoctor, isMobile]);

  const searchThisArea = useCallback(() => {
    if (!gmap.current) return;
    const c = gmap.current.getCenter();
    if (!c) return;

    const next = { lat: c.lat(), lng: c.lng() };
    setCenter(next);
    setTimeout(updateVisibleRadiusFromMap, 150);
  }, [updateVisibleRadiusFromMap]);

  const handleDoctorClick = useCallback(
    (doctor) => {
      try {
        const safeDoctor = normalizeDoctor(doctor);
        if (!safeDoctor) return;

        setSelectedDoctor(safeDoctor);
        setCenter(safeDoctor.position);

        if (gmap.current) {
          gmap.current.panTo(safeDoctor.position);
          gmap.current.setZoom(14);
          setTimeout(updateVisibleRadiusFromMap, 150);
        }

        if (isMobile) {
          setShowList(false);
        }
      } catch (err) {
        console.error("Doctor click failed:", err);
      }
    },
    [isMobile, updateVisibleRadiusFromMap]
  );

  const clearFilters = useCallback(() => {
    setSearch("");
    setSelectedSpec("");
  }, []);

  if (!user) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f8f7ff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
        }}
      >
        <div style={{ textAlign: "center", padding: 24 }}>
          <MapPin size={32} color="#c7d2fe" style={{ marginBottom: 12 }} />
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: "#4338ca",
              marginBottom: 8,
            }}
          >
            Kjo faqe kërkon hyrje
          </div>
          <button
            onClick={() => navigate("/login")}
            style={{
              background: "#6366f1",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              padding: "10px 20px",
              fontSize: 14,
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            Hyr
          </button>
        </div>
      </div>
    );
  }

  const showDesktopList = showList && !isMobile;

  return (
    <div
      style={{
        height: "100dvh",
        display: "grid",
        gridTemplateRows: "56px 1fr",
        background: "#f8f7ff",
        overflow: "hidden",
        fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      }}
    >
      <header
        style={{
          background: "#fff",
          borderBottom: "1px solid #e0e7ff",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: isMobile ? "0 12px" : "0 16px",
          zIndex: 20,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            border: "1.5px solid #e0e7ff",
            background: "#f8f7ff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={16} color="#4338ca" />
        </button>

        <div
          style={{
            fontSize: isMobile ? 14 : 15,
            fontWeight: 900,
            color: "#1e1b4b",
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginRight: "auto",
            minWidth: 0,
          }}
        >
          <MapPin size={15} color="#6366f1" />
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Harta e Mjekëve
          </span>
        </div>

        {!isMobile && filtered.length > 0 && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              background: "#eef2ff",
              border: "1px solid #c7d2fe",
              borderRadius: 20,
              padding: "4px 11px",
              fontSize: 11,
              fontWeight: 800,
              color: "#4338ca",
            }}
          >
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#6366f1",
              }}
            />
            {filtered.length} mjekë
          </div>
        )}

        <div
          style={{
            background: "#f8f7ff",
            border: "1.5px solid #e0e7ff",
            borderRadius: 10,
            padding: isMobile ? "7px 10px" : "6px 10px",
            fontSize: 12,
            fontWeight: 800,
            color: "#1e1b4b",
            whiteSpace: "nowrap",
          }}
        >
          {visibleRadiusKm} km
        </div>

        <button
          onClick={getUserLocation}
          disabled={gettingLoc}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "#6366f1",
            color: "#fff",
            border: "none",
            borderRadius: 10,
            padding: isMobile ? "7px 10px" : "7px 12px",
            fontSize: 12,
            fontWeight: 900,
            cursor: "pointer",
            opacity: gettingLoc ? 0.6 : 1,
            flexShrink: 0,
          }}
        >
          <Navigation size={13} />
          {!isMobile ? (gettingLoc ? "…" : "Pranë meje") : null}
        </button>

        {isMobile ? (
          <button
            onClick={() => setShowMobileFilters(true)}
            style={{
              background: "#f5f3ff",
              color: "#4338ca",
              border: "1.5px solid #c7d2fe",
              borderRadius: 10,
              padding: "7px 10px",
              fontSize: 12,
              fontWeight: 800,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <SlidersHorizontal size={14} />
          </button>
        ) : (
          <button
            onClick={() => setShowList((p) => !p)}
            style={{
              background: showList ? "#6366f1" : "#f5f3ff",
              color: showList ? "#fff" : "#4338ca",
              border: `1.5px solid ${showList ? "#6366f1" : "#c7d2fe"}`,
              borderRadius: 10,
              padding: "7px 10px",
              fontSize: 12,
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            <SlidersHorizontal size={14} />
          </button>
        )}
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : showDesktopList ? "320px 1fr" : "0 1fr",
          overflow: "hidden",
          transition: "grid-template-columns .3s",
          position: "relative",
          minHeight: 0,
        }}
      >
        {showDesktopList && (
          <div
            style={{
              background: "#fff",
              borderRight: "1px solid #e0e7ff",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "12px 14px",
                borderBottom: "1px solid #e0e7ff",
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: "#f8f7ff",
                  border: "1.5px solid #e0e7ff",
                  borderRadius: 10,
                  padding: "8px 12px",
                  marginBottom: 10,
                }}
              >
                <Search size={13} color="#6366f1" style={{ flexShrink: 0 }} />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Kërko emër, specialitet…"
                  style={{
                    border: "none",
                    background: "transparent",
                    outline: "none",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#1e1b4b",
                    width: "100%",
                  }}
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    style={{
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    <X size={13} color="#a5b4fc" />
                  </button>
                )}
              </div>

              <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 8 }}>
                <Chip active={!selectedSpec} onClick={() => setSelectedSpec("")}>
                  Të gjithë
                </Chip>

                {specialties.map((s) => (
                  <Chip
                    key={s.key}
                    active={selectedSpec === s.key}
                    onClick={() =>
                      setSelectedSpec(selectedSpec === s.key ? "" : s.key)
                    }
                  >
                    {s.label}
                  </Chip>
                ))}
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={searchThisArea}
                  style={{
                    flex: 1,
                    background: "#eef2ff",
                    color: "#4338ca",
                    border: "1.5px solid #c7d2fe",
                    borderRadius: 10,
                    padding: "8px 10px",
                    fontSize: 12,
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  Kërko këtu
                </button>

                <button
                  onClick={clearFilters}
                  style={{
                    background: "#fff",
                    color: "#64748b",
                    border: "1.5px solid #e2e8f0",
                    borderRadius: 10,
                    padding: "8px 10px",
                    fontSize: 12,
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  Pastro
                </button>
              </div>
            </div>

            <div
              style={{
                padding: "8px 14px",
                borderBottom: "1px solid #e0e7ff",
                fontSize: 11,
                fontWeight: 800,
                color: "#6366f1",
                flexShrink: 0,
              }}
            >
              {loading ? "Duke ngarkuar…" : `${filtered.length} mjekë brenda ${visibleRadiusKm} km`}
            </div>

            {(locErr || error) && (
              <div
                style={{
                  padding: "8px 14px",
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#dc2626",
                  background: "#fef2f2",
                  borderBottom: "1px solid #fecaca",
                  flexShrink: 0,
                }}
              >
                {locErr || error}
              </div>
            )}

            <div style={{ overflowY: "auto", flex: 1 }}>
              {filtered.length === 0 && !loading && (
                <div
                  style={{
                    padding: "24px 14px",
                    textAlign: "center",
                    color: "#94a3b8",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  Nuk u gjet asnjë mjek.
                  <br />
                  Zgjero hartën ose ndrysho filtrat.
                </div>
              )}

              {filtered.map((doctor) => (
                <DoctorCardListItem
                  key={doctor.id}
                  doctor={doctor}
                  selected={selectedDoctor?.id === doctor.id}
                  onClick={() => handleDoctorClick(doctor)}
                />
              ))}
            </div>
          </div>
        )}

        <div style={{ position: "relative", overflow: "hidden", minHeight: 0 }}>
          <div ref={mapRef} style={{ width: "100%", height: "100%" }} />

          <div
            style={{
              position: "absolute",
              right: 12,
              top: 12,
              display: "flex",
              flexDirection: "column",
              gap: 6,
              zIndex: 5,
            }}
          >
            <button
              onClick={() => gmap.current?.setZoom((gmap.current?.getZoom() || 12) + 1)}
              style={{
                width: 36,
                height: 36,
                background: "#fff",
                border: "1px solid #e0e7ff",
                borderRadius: 10,
                fontSize: 18,
                fontWeight: 900,
                color: "#4338ca",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,.06)",
              }}
            >
              +
            </button>

            <button
              onClick={() => gmap.current?.setZoom((gmap.current?.getZoom() || 12) - 1)}
              style={{
                width: 36,
                height: 36,
                background: "#fff",
                border: "1px solid #e0e7ff",
                borderRadius: 10,
                fontSize: 18,
                fontWeight: 900,
                color: "#4338ca",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,.06)",
              }}
            >
              −
            </button>
          </div>

          <div
            style={{
              position: "absolute",
              top: 12,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 5,
            }}
          >
            <button
              onClick={searchThisArea}
              style={{
                background: "#fff",
                border: "1px solid #e0e7ff",
                borderRadius: 20,
                padding: "7px 16px",
                fontSize: 12,
                fontWeight: 800,
                color: "#4338ca",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,.08)",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Search size={12} />
              Kërko këtu
            </button>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: selectedDoctor ? (isMobile ? 152 : 184) : 16,
              left: 12,
              zIndex: 5,
              background: "rgba(255,255,255,.95)",
              border: "1px solid #e0e7ff",
              borderRadius: 20,
              padding: "5px 12px",
              fontSize: 11,
              fontWeight: 800,
              color: "#4338ca",
              boxShadow: "0 2px 8px rgba(0,0,0,.06)",
              transition: "bottom .25s",
            }}
          >
            Rrezja: {visibleRadiusKm} km · {filtered.length} mjekë
          </div>

          {(locErr || error) && isMobile && (
            <div
              style={{
                position: "absolute",
                top: 58,
                left: 12,
                right: 56,
                zIndex: 5,
                background: "#fef2f2",
                color: "#dc2626",
                border: "1px solid #fecaca",
                borderRadius: 12,
                padding: "8px 10px",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {locErr || error}
            </div>
          )}

          {!loading && filtered.length === 0 && !error && (
            <div
              style={{
                position: "absolute",
                top: isMobile ? 58 : 56,
                left: 12,
                right: 56,
                zIndex: 5,
                background: "rgba(255,255,255,.96)",
                border: "1px solid #e0e7ff",
                borderRadius: 14,
                padding: "10px 12px",
                fontSize: 12,
                fontWeight: 700,
                color: "#64748b",
                boxShadow: "0 2px 8px rgba(0,0,0,.06)",
              }}
            >
              Nuk u gjet asnjë mjek për zonën aktuale.
            </div>
          )}

          <DoctorPopup
            doctor={selectedDoctor}
            isMobile={isMobile}
            navigate={navigate}
            onClose={() => setSelectedDoctor(null)}
          />

          {!GOOGLE_MAPS_API_KEY && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#f8f7ff",
              }}
            >
              <div style={{ textAlign: "center", color: "#94a3b8" }}>
                <MapPin size={32} color="#c7d2fe" style={{ marginBottom: 8 }} />
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  REACT_APP_GOOGLE_MAPS_API_KEY mungon
                </div>
              </div>
            </div>
          )}

          <MobileFiltersDrawer
            open={showMobileFilters}
            onClose={() => setShowMobileFilters(false)}
            search={search}
            setSearch={setSearch}
            specialties={specialties}
            selectedSpec={selectedSpec}
            setSelectedSpec={setSelectedSpec}
            onClear={clearFilters}
          />
        </div>
      </div>
    </div>
  );
}